import { sendNotification, type SendNotificationResponse } from "../../../../api/sendNotification";
import { useSalesContext } from "../../../../sales-context/SalesContext";
import { useProductOfferStore } from "../../../../stores";

/**
 * Track sessions where compliance event has already been sent to prevent duplicate calls.
 * This prevents re-sending the event if sendNotification fails and the expert retries.
 * Module-level to persist across component unmounts/remounts.
 */
const complianceEventSentSessions = new Set<string>();

export const useHandleSendLink = ({ isGuidedBrowsing }: { isGuidedBrowsing?: boolean } = {}) => {
    const {
        sessionId,
        agentId,
        partner,
        logger,
        analytics,
        taskType,
        taskData,
        activeChatId,
        apiClients,
        gaiaWebsocket,
        features,
    } = useSalesContext();

    const isRealTimeComplianceCheckEnabled = features.isRealTimeComplianceCheckEnabled;

    const primaryProducts = useProductOfferStore((state) => state.getPrimaryProducts(sessionId));
    const selectedProduct = useProductOfferStore((state) => state.getSelectedProduct(sessionId));

    if (primaryProducts == null) {
        throw new Error("Expected data is undefined");
    }

    if (!taskType.isVoice && !taskType.isChat) {
        return () => Promise.reject(new Error("Send link is only available for voice or chat tasks"));
    }

    const sendComplianceEvent = () => {
        if (!taskType.isVoice || !isRealTimeComplianceCheckEnabled || !gaiaWebsocket || gaiaWebsocket.loading) {
            return;
        }

        if (complianceEventSentSessions.has(sessionId)) {
            logger.debug("Compliance event already sent for this session, skipping");
            return;
        }

        const gaiaPayload = {
            action: "orchestrator-invoke",
            appName: "soluto-sales-compliance-cue",
            messageType: "Notification",
            correlationId: crypto.randomUUID(),
            messageId: crypto.randomUUID(),
            message: "",
            sessionId: activeChatId ?? sessionId,
            sessionGroupId: sessionId,
            userId: agentId,
            userType: "Expert",
            partner,
        };

        try {
            gaiaWebsocket.sendJsonMessage(gaiaPayload);
            complianceEventSentSessions.add(sessionId);
            logger.info("Successfully sent compliance validation event to GAIA");
        } catch (error) {
            logger.warn({ error }, "Failed to send compliance validation event to GAIA");
        }
    };

    return async (commType: string, contactToSend: string): Promise<SendNotificationResponse> => {
        sendComplianceEvent();

        const product = selectedProduct ?? primaryProducts[0];
        // A re-check moved the selection to a new quote since the expert picked it. Read before the
        // await so it describes the id being sent: a re-check landing mid-request must not make the
        // log claim a refreshed send for the id that actually went out.
        const previousProductOfferId = selectedProduct
            ? useProductOfferStore.getState().getReplacedSelectedProductOfferId(sessionId)
            : undefined;
        const response = await sendNotification(apiClients.sales, logger, analytics.dispatcher, {
            sessionId,
            partner,
            productOfferId: product.id,
            productSku: product.productSku,
            customerEmailId: commType === "EMAIL" && contactToSend ? contactToSend : undefined,
            customerMdn: commType === "SMS" && contactToSend ? contactToSend : undefined,
            customerChatId: activeChatId,
            workerSid: agentId,
            topic: taskType.isChat ? taskData.chatTopic : undefined,
            language: taskType.isChat ? taskData.chatLanguage : undefined,
            guidedBrowsing: isGuidedBrowsing ? true : undefined,
            // Same product the offer id comes from, so the stamp always matches the offer.
            salesProvider: product.salesProvider,
        });

        // Record that the link went out on the refreshed quoteId (the stale one is what used to 500 in
        // updateQuotes).
        if (previousProductOfferId) {
            logger.info(
                {
                    sessionId,
                    productSku: product.productSku,
                    previousProductOfferId,
                    productOfferId: product.id,
                    salesProvider: product.salesProvider,
                },
                "Sent link with refreshed quoteId",
            );
        }

        return response;
    };
};
